import React, { useState } from 'react';
import {
    Box,
    Collapse,
    FormControl,
    FormControlProps,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    NativeSelect,
    Select,
    SelectChangeEvent
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import useMyDropdowns from 'hooks/useMyDropdowns';
import { SystemStyleObject } from '@mui/system';

export interface ProblemCodeDropdownProps {
    onChange: (value: string | string[]) => void;
    recordTypeId: number;
    initialValue?: string | string[];
    variant?: FormControlProps['variant'];
    size?: FormControlProps['size'];
    label?: string;
    native?: boolean;
    multiple?: boolean;
    fromFilterPanel?: boolean;
    hideLabel?: boolean;
    inputSx?: SystemStyleObject;
}

export const ProblemCodeDropdown = ({
    onChange,
    recordTypeId,
    // initialValue could be the name or the id depending on where is used
    initialValue,
    native,
    label,
    variant = 'outlined',
    size = 'medium',
    multiple = false,
    fromFilterPanel = false,
    hideLabel = false,
    inputSx = {}
}: ProblemCodeDropdownProps) => {
    const [problemCodeSelected, setProblemCodeSelected] = useState('');
    const [problemCodeMultipleSelected, setProblemCodeMultipleSelected] = useState<string[]>([]);

    const { loading, problemCodeList } = useMyDropdowns({
        recordTypeId,
        dropdowns: ['PROBLEM_CODE'],
        onCompleted(data) {
            if (data.problemCodeList && initialValue) {
                if (fromFilterPanel) {
                    if (Array.isArray(initialValue)) setProblemCodeMultipleSelected(initialValue as string[]);
                    else setProblemCodeSelected(initialValue as string);
                } else if (multiple) {
                    const values = data.problemCodeList.map((el) => String(el.id)).filter((el) => initialValue?.includes(el));
                    setProblemCodeMultipleSelected(values || []);
                } else if (!Number(initialValue)) {
                    const value = data.problemCodeList.find(({ name }) => (initialValue as string) === name)?.id;
                    setProblemCodeSelected(String(value) || '');
                } else {
                    setProblemCodeSelected(String(initialValue) || '');
                }
            }
        }
    });

    const handleValueChange = (event: SelectChangeEvent<string | string[]> | React.ChangeEvent<{ value: string }>) => {
        const newValue = event.target.value;
        if (multiple) setProblemCodeMultipleSelected(newValue as string[]);
        else setProblemCodeSelected(newValue as string);
        onChange(newValue);
    };

    const handleClearClick = () => {
        const newValue = multiple ? ['0'] : '0';
        if (multiple) setProblemCodeMultipleSelected([newValue] as string[]);
        else setProblemCodeSelected(newValue as string);
        onChange(newValue);
    };

    return (
        <Box className="problem-code-dropdown-container" sx={{ minWidth: 149 }}>
            <FormControl fullWidth disabled={loading}>
                {label && !hideLabel && (
                    <InputLabel id="problem-code-select" shrink>
                        {label}
                    </InputLabel>
                )}
                {native ? (
                    <NativeSelect value={problemCodeSelected} onChange={handleValueChange} sx={inputSx}>
                        <option value=""> &nbsp;</option>
                        {problemCodeList?.map((el) => (
                            <option key={el.id} value={el.id}>
                                {el.name}
                            </option>
                        ))}
                    </NativeSelect>
                ) : (
                    <Select
                        value={multiple ? problemCodeMultipleSelected : problemCodeSelected}
                        onChange={handleValueChange}
                        variant={variant}
                        size={size}
                        sx={{ pt: '3px !important', ...inputSx }}
                        multiple={multiple}
                        endAdornment={
                            <IconButton
                                sx={{
                                    display: !multiple && problemCodeSelected && problemCodeSelected !== '0' ? '' : 'none'
                                }}
                                onClick={handleClearClick}
                            >
                                <Clear />
                            </IconButton>
                        }
                        {...(!multiple && problemCodeSelected && problemCodeSelected !== '0' && { IconComponent: () => null })}
                    >
                        {problemCodeList?.map((el) => (
                            <MenuItem key={el.id} value={el.id}>
                                {el.name}
                            </MenuItem>
                        ))}
                    </Select>
                )}
                <Collapse in={loading}>
                    <LinearProgress />
                </Collapse>
            </FormControl>
        </Box>
    );
};
